/**
* Recommendation API calls for DBCOM.
*/
(function () {
  // Attach API to dom.
  window.dbcom = window.dbcom || {}
  window.dbcom.recommendations = {
    autosuggest,
    saveSearch,
  }

  // Recommendation Constants
  const HISTORY_STORAGE_KEY = 'dbcom:search:history'
  const MAX_HISTORY_STORAGE = 25
  const FILLER_WORDS = ['a', 'am', 'an', 'and', 'of', 'he', 'i', 'it', 'she', 'the', 'them', 'they', 'you']

  /**
  * Fetches autosuggest results.
  *
  * @param args {object} object containing `keywords`, `type`, and `count`.
  * @param onSuccess {function} success callback.
  * @param onFail {function} fail callback.
  */
  function autosuggest(args, onSuccess, onFail) {
    const keywords = args.keywords ? args.keywords.trim() : ''
    const type = args.type || 'product'
    const count = args.count || 5
    const path = '/autosuggest'
    const params = {}

    // Determine params to send based on type.
    switch (type) {
      case 'history':
        var results = _getFromHistory(keywords, count)

        return (typeof onSuccess === 'function') ? onSuccess(results) : results
      case 'taxon':
        params.taxon = keywords
        break
      default:
        params.keywords = keywords
        break
    }

    // Fetch results, and format if needed.
    dbcom.fetch(path, params, (data) => {
      let recommendations = data

      // If `strict` is set, only accept recommendations that contain at least one
      // of the keywords.
      if (args.strict) {
        const escapedValue = _escapeRegexCharacters(keywords)

        if (escapedValue) {
          // Allow any recommendation matching at least one word.
          const regex = _getSearchRegex(escapedValue)

          recommendations = recommendations
            .filter(datum => regex.test(datum.value))
        }
      }

      // If `count` is given, limit the number of results to that number.
      if (args.count) {
        recommendations = recommendations
          .filter((datum, idx) => idx < args.count)
      }

      // Format taxon recommendations for better grouping.
      if (type === 'taxon') {
        recommendations = _formatTaxonRecommendations(recommendations)
      }

      // Perform callback if one is present, otherwise just return the data.
      if (typeof onSuccess === 'function') {
        return onSuccess(recommendations)
      }

      return recommendations
    }, onFail)
  }

  /**
  * Saves a search term to the user's `localStorage` for a historical record
  * of searches.
  *
  * @param value {string} Search term.
  * @param slug {string} Search term destination.
  * @param onSuccess {func} onSuccess callback function.
  */
  function saveSearch(value, slug, onSuccess) {
    if (localStorage && value) {
      const key = value.trim().toLowerCase()
      let history = JSON.parse(localStorage.getItem(HISTORY_STORAGE_KEY)) || []
      let historyRecord
      const currentIndex = history
        .map(record => record.id)
        .indexOf(key)

      // If it's there, bring it to the front and update the search count.
      if (currentIndex > -1) {
        historyRecord = history.splice(currentIndex, 1)[0]

        // Add the newest date record to the front.
        if (historyRecord.history) {
          historyRecord.history.splice(0, 0, String(new Date()))
        }
      // If it's not there, then add it to the front.
      } else {
        historyRecord = {
          id: key,
          type: 'history',
          label: value,
          value,
          slug,
          history: [String(new Date())],
        }
      }
      // Add the history record back to the front.
      history.splice(0, 0, historyRecord)

      // Make sure the length of history doesn't exceed the max.
      history = history.slice(0, MAX_HISTORY_STORAGE)
      // Update history in storage.
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history))
    }

    if (typeof onSuccess === 'function') {
      onSuccess()
    }
  }

  /**
  * Escapes special characters for proper regex testing.
  *
  * @see https://developer.mozilla.org/en/docs/Web/JavaScript/Guide/Regular_Expressions#Using_Special_Characters
  */
  function _escapeRegexCharacters(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }

  /**
  * Builds a `RegExp` that when using the `test` method will return true for
  * all strings where at least one word in the string is present in the regex value,
  * excluding any words in the `FILLER_WORDS` variable.
  *
  * @param {value} value to compare against.
  * @returns {RegExp}
  */
  function _getSearchRegex(value) {
    // Remove white spaces and filler words.
    const regexValue = value
      .trim()
      .split(' ')
      .filter(word => FILLER_WORDS.indexOf(word.toLowerCase()) < 0)
      .join('|')

    return new RegExp(regexValue, 'i')
  }

  /**
  * Returns user's recent search queries matching the given `value`.
  *
  * @param value {string} search query to match.
  * @param count {number} Max number of results to return.
  * @return {array[string]} Search queries matching `value`.
  */
  function _getFromHistory(value, count) {
    count = count || 3 // Default to pulling the top 3 results.
    const history = localStorage ? JSON.parse(localStorage.getItem(HISTORY_STORAGE_KEY)) || [] : []
    const escapedValue = _escapeRegexCharacters(value.trim())

    if (escapedValue) {
      // Allow any recommendation matching at least one word.
      const regex = _getSearchRegex(escapedValue)

      return history
        .filter(record => regex.test(record.value))
        .slice(0, count)
    }

    return []
  }

  /**
  * Formats taxon recommendations so they are grouped together and have additional
  * info needed for rendering them properly.
  *
  * @param data {array} list of taxon recommendations.
  */
  function _formatTaxonRecommendations(data) {
    // First group taxons with the same name, grouping taxons and taxonomies separately.
    const suggestions = data
      .reduce((taxons, taxon) => {
        const key = taxon.value.trim().toLowerCase()

        // If the current suggestion isn't already there, then add it.
        if (!(key in taxons)) {
          taxons[key] = {
            label: taxon.label.trim(),
            value: taxon.value.trim(),
            taxons: [],
          }
        }

        // If there are no crumbs present, then this must be a taxonomy,
        // otherwise add it to the taxons.
        if (!taxon.crumbs.length) {
          taxons[key].taxonomy = taxon
        } else {
          taxons[key].taxons.push(taxon)
        }

        return taxons
      }, {})

    // Now flatten the suggestions for rendering.
    let flattenedSuggestions = []

    Object.keys(suggestions)
      .map((key) => {
        const suggestion = suggestions[key]

        // Sort child taxons first.
        suggestion.taxons = suggestion.taxons
          .sort((a, b) => a.score < b.score)
          .map((taxon) => {
            // If there are more than one grouped taxons, then change the `type`
            // to reflect these taxons are grouped together. This helps
            // distinguish them for different rendering.
            if (suggestion.taxons.length < 2) {
              return taxon
            }
            taxon.type = 'nested'

            return taxon
          })

        // If there's a taxonomy, then add that as the first choice.
        // If there's no taxonomy, but there are multiple taxons, then add
        // a search result as the first choice.
        if (suggestion.taxonomy) {
          suggestion.taxonomy.type = 'taxonomy'
          flattenedSuggestions.push(suggestion.taxonomy)
        } else if (suggestion.taxons.length > 1) {
          flattenedSuggestions.push({ type: 'group', value: suggestion.value, label: suggestion.label })
        }

        // Now add all of the taxons back.
        flattenedSuggestions = flattenedSuggestions.concat(suggestion.taxons)
      })

    return flattenedSuggestions
  }
}())
